import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import InputBase from '@mui/material/InputBase';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import { flattenSidebarConfig, filterItems } from './searchUtils';
import SearchResultsList from './SearchResultsList';

const SearchCommandDialog = ({ open, onClose, sidebarConfig, changeSetting }) => {
	const [query, setQuery] = useState('');
	const [activeIndex, setActiveIndex] = useState(0);
	const inputRef = useRef(null);
	const listRef = useRef(null);

	const allItems = useMemo(() => flattenSidebarConfig(sidebarConfig), [sidebarConfig]);
	const filteredItems = useMemo(() => filterItems(allItems, query), [allItems, query]);

	useEffect(() => {
		if (open) {
			setQuery('');
			setActiveIndex(0);
			setTimeout(() => inputRef.current?.focus(), 50);
		}
	}, [open]);

	// Reset active index when filtered results change
	useEffect(() => {
		setActiveIndex(0);
	}, [filteredItems.length]);

	useEffect(() => {
		if (listRef.current) {
			const activeEl = listRef.current.querySelector(`[data-index="${activeIndex}"]`);
			if (activeEl) {
				activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
			}
		}
	}, [activeIndex]);

	const handleSelect = useCallback(
		(settingName) => {
			changeSetting(settingName);
			onClose();
		},
		[changeSetting, onClose]
	);

	const handleKeyDown = (e) => {
		if (['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) {
			e.preventDefault();
			if (e.key === 'ArrowDown') {
				if (!filteredItems.length) return;
				setActiveIndex((prev) => Math.min(prev + 1, filteredItems.length - 1));
			} else if (e.key === 'ArrowUp') {
				if (!filteredItems.length) return;
				setActiveIndex((prev) => Math.max(prev - 1, 0));
			} else if (e.key === 'Enter') {
				if (filteredItems[activeIndex]) {
					handleSelect(filteredItems[activeIndex].settingName);
				}
			}
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: '12px',
					overflow: 'hidden',
					boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
					maxHeight: '480px',
					marginTop: '-10vh'
				}
			}}
			slotProps={{
				backdrop: {
					sx: {
						backgroundColor: 'rgba(0, 0, 0, 0.4)',
						backdropFilter: 'blur(4px)'
					}
				}
			}}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					padding: '12px 16px',
					borderBottom: '1px solid #e0e0e0',
					gap: '10px'
				}}
			>
				<SearchIcon sx={{ color: '#9e9e9e', fontSize: '22px' }} />
				<InputBase
					inputRef={inputRef}
					placeholder="Search settings..."
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					onKeyDown={handleKeyDown}
					fullWidth
					sx={{
						fontSize: '15px',
						'& input': { padding: '4px 0' }
					}}
				/>
				<Typography
					component="span"
					sx={{
						fontSize: '11px',
						color: '#9e9e9e',
						border: '1px solid #e0e0e0',
						borderRadius: '4px',
						padding: '2px 6px',
						whiteSpace: 'nowrap',
						userSelect: 'none'
					}}
				>
					ESC
				</Typography>
			</Box>
			<DialogContent
				sx={{
					padding: '0 !important',
					overflowY: 'auto',
					maxHeight: '380px'
				}}
			>
				<SearchResultsList
					listRef={listRef}
					filteredItems={filteredItems}
					activeIndex={activeIndex}
					handleSelect={handleSelect}
				/>
			</DialogContent>
		</Dialog>
	);
};

export default SearchCommandDialog;
